import type { ModalData } from '../../common/events/types'
import { MODALS, BillingInterval } from '../../common/types'
import type { Account, PromiseCB } from '../../common/types'
import { showModal } from '../../exports/Orchestrator/store/modalsReducer'
import store from '../../exports/Orchestrator/store/orchestratorStore'
import { getBillingPageUrl, redirectToUrl } from '../../common/utils/urls'
import {
  hasPermissionToManageBilling,
  userCanStartFreeTrial,
} from '../../common/utils/user'
import {
  START_TRIAL_CTA_QUERY_PARAM,
  START_TRIAL_DEEPLINK_KEY,
  START_TRIAL_INTENT,
  START_TRIAL_INTENT_QUERY_PARAM,
} from '../../common/utils/stashUrlDeeplinks'
import { isEligibleForAnnualUpgrade } from '../../exports/Navigator/utils/shouldShowAnnualUpgradeBanner'

type RefetchAccountIfNeededProps = {
  modalKey: string
  refetchAccountData: PromiseCB
}

export async function refetchAccountIfNeeded({
  modalKey,
  refetchAccountData,
}: RefetchAccountIfNeededProps): Promise<void> {
  // This was added to fix the data sync when
  // the ChannelConnectionUpgradeModal was being call by the account app
  // We need to make sure even if users had removed channels etc
  // we refetched the latest data

  if (
    modalKey === MODALS.connectChannelUpgrade ||
    modalKey === MODALS.channelConnectionSuccessModal
  ) {
    await refetchAccountData()
  }
}

/**
 * This function is used to handle the modal event
 * It is called when the modal event is triggered
 */
export const handleModalEvent = async (
  event: Event,
  refetchAccountData: PromiseCB,
): Promise<void> => {
  const { modal: modalKey, data: modalData } = (
    event as CustomEvent<{ modal: string; data?: ModalData }>
  ).detail

  await refetchAccountIfNeeded({ modalKey, refetchAccountData })

  store.dispatch(showModal({ modalKey, modalData }))
}

type HandleOpenModalProps = {
  openModal: (props: { key: string; data?: ModalData }) => void
}

const DEFAULT_PRICING_TRIAL_CTA = 'pricingPage-marketingSite-startTrial-1'

function cleanupStartTrialIntent(): void {
  const urlParams = new URLSearchParams(window.location.search)
  const intentValues = urlParams.getAll(START_TRIAL_INTENT_QUERY_PARAM)

  if (!intentValues.includes(START_TRIAL_INTENT)) {
    return
  }

  const hasOtherIntent = intentValues.some(
    (intent) => intent !== START_TRIAL_INTENT,
  )
  const remainingParams = new URLSearchParams()

  urlParams.forEach((value, key) => {
    if (
      (key === START_TRIAL_INTENT_QUERY_PARAM &&
        value === START_TRIAL_INTENT) ||
      (key === START_TRIAL_CTA_QUERY_PARAM && !hasOtherIntent)
    ) {
      return
    }

    remainingParams.append(key, value)
  })

  const search = remainingParams.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`,
  )
}

type HandleStartTrialIntentProps = HandleOpenModalProps & {
  user: Account
}

export function handleStartTrialIntentViaURL({
  openModal,
  user,
}: HandleStartTrialIntentProps): void {
  const cta = sessionStorage.getItem(START_TRIAL_DEEPLINK_KEY)
  const billing = user.currentOrganization?.billing
  const organizationActions = user.currentOrganization?.allowedActions
  const hasTrialEligibilityData =
    billing?.canStartTrial !== undefined || billing?.subscriptions !== undefined

  if (
    cta === null ||
    organizationActions === undefined ||
    !hasTrialEligibilityData
  ) {
    return
  }

  sessionStorage.removeItem(START_TRIAL_DEEPLINK_KEY)
  cleanupStartTrialIntent()

  if (!userCanStartFreeTrial(user) || !hasPermissionToManageBilling(user)) {
    return
  }

  openModal({
    key: MODALS.startTrial,
    data: {
      cta: cta || DEFAULT_PRICING_TRIAL_CTA,
      ctaButton: 'startTrial',
      ctaView: 'pricingPage',
      ctaLocation: 'marketingSite',
      ctaVersion: '1',
      upgradePathName: 'marketingPricing-trial',
    },
  })
}

export function handleChannelConnectionOnboardingViaURL({
  openModal,
}: HandleOpenModalProps): void {
  const urlParams = new URLSearchParams(window.location.search)
  const channelIds = urlParams.getAll('channelId')
  const ctaFromURL = urlParams.getAll('cta')

  let ctaView, ctaLocation, ctaButton, ctaVersion

  if (ctaFromURL.length > 0) {
    ;[ctaView, ctaLocation, ctaButton, ctaVersion] = ctaFromURL[0].split('-')
  } else {
    ctaView = 'queue'
    ctaLocation = 'connectChannelOnboardingModal'
    ctaButton = 'createAPost'
    ctaVersion = '1'
  }

  if (channelIds.length > 0) {
    openModal({
      key: MODALS.channelConnectionSuccessModal,
      data: {
        ctaView,
        ctaLocation,
        ctaButton,
        ctaVersion,
        channelIds,
        upgradePathName: 'connectChannel-upgrade',
      },
    })
  }
}

export function handleChannelRefreshViaURL({
  openModal,
}: HandleOpenModalProps): void {
  const urlParams = new URLSearchParams(window.location.search)
  const channelIds = urlParams.getAll('channelId')
  const channelName = urlParams.get('channelName')
  const channelAvatar = urlParams.get('channelAvatar')
  const ctaFromURL = urlParams.getAll('cta')

  let ctaView, ctaLocation, ctaButton, ctaVersion

  if (ctaFromURL.length > 0) {
    ;[ctaView, ctaLocation, ctaButton, ctaVersion] = ctaFromURL[0].split('-')
  } else {
    ctaView = 'queue'
    ctaLocation = 'channelRefreshSuccessModal'
    ctaButton = 'createAPost'
    ctaVersion = '1'
  }

  // Handle regular channel refresh success
  if (channelIds.length > 0) {
    openModal({
      key: MODALS.channelRefreshSuccessModal,
      data: {
        ctaView,
        ctaLocation,
        ctaButton,
        ctaVersion,
        channelIds,
        upgradePathName: 'connectChannel-upgrade',
      },
    })
  }
  // Handle wrong channel refresh case (no actual channels refreshed but we want to show wrongChannel modal)
  else if (channelName && channelAvatar) {
    openModal({
      key: MODALS.channelRefreshSuccessModal,
      data: {
        ctaView,
        ctaLocation,
        ctaButton,
        ctaVersion,
        channelIds,
        channelAvatar,
        channelName,
        upgradePathName: 'connectChannel-upgrade',
      },
    })
  }
}

type HandleAnnualUpgradeDeeplinkProps = {
  openModal: (props: { key: string; data?: ModalData }) => void
  user: Account
}

export function handleAnnualUpgradeDeeplinkViaURL({
  openModal,
  user,
}: HandleAnnualUpgradeDeeplinkProps): void {
  const subscription = user.currentOrganization?.billing?.subscription

  // Always clear the flag first to prevent re-triggering
  sessionStorage.removeItem('annualUpgradeDeeplink')

  // Already on annual plan → nothing to do (do not redirect; user is not "ineligible" in a recoverable way)
  if (subscription?.interval === BillingInterval.Year) {
    return
  }

  // The Customer.io email is the cohort gate, so the deeplink itself does not
  // re-check the day threshold (otherwise the 2-month arm would 403 here).
  if (!isEligibleForAnnualUpgrade(user, 0)) {
    redirectToUrl(getBillingPageUrl(null))
    return
  }

  openModal({
    key: MODALS.annualUpgrade,
    data: {
      cta: 'annualUpgradeModal-deeplink',
      ctaButton: 'switchToAnnual',
      ctaView: 'annualUpgradeModal',
      ctaLocation: 'deeplink',
      ctaVersion: '1',
      upgradePathName: 'annualUpgrade',
    },
  })
}
